import { useQuery } from '@tanstack/react-query';
import {
  createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState,
} from 'react';
import { useSearchParams } from 'react-router-dom';
import { NO_CATEGORY } from '../../constants/categories';
import { GuildApi } from '../../services/guild-api';
import { WebshopApi } from '../../services/webshop-api';
import { OfferType } from '../../types/offer-type';
import { BaseOffer, Member } from '../../types/offers';
import { useCurrencies } from '../currencies-context';
import { useUserInfo } from '../user-info';

const SELECTED_OFFER_TYPE_STORAGE_KEY = 'selectedOfferType';
const DEFAULT_RETRY_COUNT = 2;
const DEFAULT_REFETCH_INTERVAL = 60000;
const DEFAULT_OFFERS_QUERY_PARAMS = {
  retry: DEFAULT_RETRY_COUNT,
  refetchInterval: DEFAULT_REFETCH_INTERVAL,
};

const REGULAR_CATEGORY_ORDER = [
  'special_offers',
  'seasonal_offers',
  'daily_market',
  'tomes_market',
  'guilds',
  'currencies',
  'power_chests',
  'bounties',
] as const;
// todo: unify this later, now Sid want to have this so less testing is needed
const GUILD_CATEGORY_ORDER = [
  'special_offers',
  'seasonal_offers',
  'daily_market',
  'tomes_market',
  'guilds',
  'currencies',
  'power_chests',
  'bounties',
] as const;

function groupOffersByCategory(
  offers: BaseOffer[],
): Map<string, BaseOffer[]> {
  return offers.reduce((acc, offer) => {
    const category = offer.category ?? NO_CATEGORY;

    if (!acc.has(category)) {
      acc.set(category, []);
    }

    acc.get(category)?.push(offer);

    return acc;
  }, new Map<string, BaseOffer[]>());
}

function getAvailableCategories(
  categoryOrder: readonly string[],
  groupedOffers: Map<string, BaseOffer[]>,
): string[] {
  const categories = categoryOrder.reduce<string[]>((acc, category) => {
    const categoryOffers = groupedOffers.get(category);
    if (categoryOffers?.length) {
      acc.push(category);
    }
    return acc;
  }, []);

  const noCategoryOffers = groupedOffers.get(NO_CATEGORY);
  if (noCategoryOffers?.length) {
    categories.push(NO_CATEGORY);
  }

  return categories;
}

type OffersContextProps = {
  selectedCategory?: string,
  setSelectedCategory: (category?: string) => void,
  selectedOfferType: OfferType,
  setSelectedOfferType: (offerType: OfferType) => void,
  selectedCategories: string[],
  selectedGroupedOffers: Map<string, BaseOffer[]>,
  availableCategories: string[],
  availableGuildCategories: string[],
  regularOffers: BaseOffer[],
  guildOffers: BaseOffer[],
  isOffersLoading: boolean,
  isGuildFounder: boolean,
  guildMembers: Member[],
  isCryptoOffersAvailable: boolean,
  isFiatOffersAvailable: boolean,
  refetchRegularOffers: () => Promise<BaseOffer[]>,
  refetchGuildOffers: () => Promise<BaseOffer[]>,
}

const OffersContext = createContext<OffersContextProps | undefined>(undefined);

export const OffersProvider = ({ children }: { children: ReactNode }) => {
  const [searchParams] = useSearchParams();
  const [selectedOfferType, setSelectedOfferType] = useState<OfferType>(
    searchParams.get('type') as OfferType
    ?? localStorage.getItem(SELECTED_OFFER_TYPE_STORAGE_KEY) as OfferType
    ?? 'regular',
  );
  const [selectedCategory, setSelectedCategory] = useState<string>();

  const { selectedCurrency, selectedCountryInfo } = useCurrencies();
  const isFiat = selectedCurrency === 'FIAT';
  const { data: userInfo, isLoading: isUserInfoLoading } = useUserInfo();

  const { data: guildOverview, isLoading: isGuildOverviewLoading } = useQuery({
    queryKey: ['guildOverview'],
    queryFn: GuildApi.guildOverview,
    enabled: !!userInfo?.isGuildFounder,
  });

  const isGuildFounder = !!userInfo?.isGuildFounder && !!guildOverview?.isManagementAllowed;

  const {
    data: cryptoRegularOffers,
    isLoading: isCryptoRegularOffersLoading,
    refetch: refetchCryptoRegularOffers,
  } = useQuery({
    queryKey: ['offers', 'POWER'],
    queryFn: WebshopApi.getRegularOffers,
    ...DEFAULT_OFFERS_QUERY_PARAMS,
  });

  const {
    data: fiatRegularOffers,
    isLoading: isFiatRegularOffersLoading,
    refetch: refetchFiatRegularOffers,
  } = useQuery({
    queryKey: ['offers', 'FIAT', selectedCountryInfo.code],
    queryFn: () => WebshopApi.getFiatOffers(selectedCountryInfo.code),
    ...DEFAULT_OFFERS_QUERY_PARAMS,
  });

  const {
    data: fiatGuildOffers,
    isLoading: isFiatGuildOffersLoading,
    refetch: refetchFiatGuildOffers,
  } = useQuery({
    queryKey: ['offers', 'FIAT', 'guild'],
    queryFn: () => WebshopApi.getGuildFiatOffers(selectedCountryInfo.code),
    ...DEFAULT_OFFERS_QUERY_PARAMS,
  });

  const {
    data: cryptoGuildOffers,
    isLoading: isCryptoGuildOffersLoading,
    refetch: refetchCryptoGuildOffers,
  } = useQuery({
    queryKey: ['offers', 'guild'],
    queryFn: WebshopApi.getGuildOffers,
    ...DEFAULT_OFFERS_QUERY_PARAMS,
  });

  const guildMembers = useMemo(() => cryptoGuildOffers?.members ?? [], [cryptoGuildOffers]);

  const isOffersLoading = useMemo(
    () => isCryptoRegularOffersLoading
      || isFiatRegularOffersLoading
      || isCryptoGuildOffersLoading
      || isFiatGuildOffersLoading,
    [
      isCryptoRegularOffersLoading,
      isFiatRegularOffersLoading,
      isCryptoGuildOffersLoading,
      isFiatGuildOffersLoading,
    ],
  );

  useEffect(() => {
    if (selectedOfferType === 'guild' || isUserInfoLoading || isGuildOverviewLoading) {
      return;
    }

    if (guildOverview?.isManagementAllowed) {
      setSelectedOfferType('regular');
    }
  }, [isUserInfoLoading, isGuildOverviewLoading, guildOverview]);

  useEffect(() => {
    localStorage.setItem(SELECTED_OFFER_TYPE_STORAGE_KEY, selectedOfferType);
  }, [selectedOfferType]);

  const regularOffers: BaseOffer[] = useMemo(
    () => (isFiat
      ? fiatRegularOffers?.offers?.map((offer) => ({ ...offer, price: offer.price / 100 })) ?? []
      : cryptoRegularOffers?.offers ?? []),
    [selectedCurrency, fiatRegularOffers, cryptoRegularOffers],
  );

  const guildOffers: BaseOffer[] = useMemo(
    () => (isFiat
      ? fiatGuildOffers?.offers?.map((offer) => ({ ...offer, price: offer.price / 100 })) ?? []
      : cryptoGuildOffers?.offers ?? []),
    [selectedCurrency, fiatGuildOffers, cryptoGuildOffers],
  );

  const groupedRegularOffers = useMemo(() => groupOffersByCategory(regularOffers), [regularOffers]);

  const groupedGuildOffers = useMemo(() => groupOffersByCategory(guildOffers), [guildOffers]);

  const availableCategories = useMemo(
    () => getAvailableCategories(REGULAR_CATEGORY_ORDER, groupedRegularOffers),
    [groupedRegularOffers],
  );

  const availableGuildCategories = useMemo(
    () => getAvailableCategories(GUILD_CATEGORY_ORDER, groupedGuildOffers),
    [groupedGuildOffers],
  );

  const { selectedCategories, selectedGroupedOffers } = useMemo(
    () => (selectedOfferType === 'guild' && isGuildFounder
      ? { selectedCategories: availableGuildCategories, selectedGroupedOffers: groupedGuildOffers }
      : { selectedCategories: availableCategories, selectedGroupedOffers: groupedRegularOffers }),
    [
      selectedOfferType,
      isGuildFounder,
      availableGuildCategories,
      availableCategories,
      groupedGuildOffers,
      groupedRegularOffers,
    ],
  );

  const refetchRegularOffers = useCallback(async () => {
    const { data } = await (isFiat ? refetchFiatRegularOffers() : refetchCryptoRegularOffers());

    return data?.offers ?? [];
  }, [isFiat]);

  const refetchGuildOffers = useCallback(async () => {
    const { data } = await (isFiat ? refetchFiatGuildOffers() : refetchCryptoGuildOffers());

    return data?.offers ?? [];
  }, [isFiat]);

  const isCryptoOffersAvailable = !!cryptoRegularOffers?.offers.length || !!cryptoGuildOffers?.offers.length;

  const isFiatOffersAvailable = !!fiatRegularOffers?.offers.length;

  const providerValue = useMemo(() => ({
    selectedOfferType,
    setSelectedOfferType,
    selectedCategory,
    setSelectedCategory,
    selectedCategories,
    selectedGroupedOffers,
    availableCategories,
    availableGuildCategories,
    regularOffers,
    guildOffers,
    isOffersLoading,
    isGuildFounder,
    guildMembers,
    refetchRegularOffers,
    refetchGuildOffers,
    isCryptoOffersAvailable,
    isFiatOffersAvailable,
  }), [
    selectedOfferType,
    selectedCategory,
    selectedCategories,
    selectedGroupedOffers,
    availableCategories,
    availableGuildCategories,
    regularOffers,
    guildOffers,
    isOffersLoading,
    isGuildFounder,
    guildMembers,
    isCryptoOffersAvailable,
    isFiatOffersAvailable,
  ]);

  return (
    <OffersContext.Provider value={providerValue}>
      {children}
    </OffersContext.Provider>
  );
};

export const useOffers = () => {
  const context = useContext(OffersContext);

  if (!context) {
    throw new Error('useOffers must be used within a OffersContext');
  }

  return context;
};
